import type { AppEntry } from '@/constants/apps'

import { apps } from '@/constants/apps'
import { appAliases, findApp, groupOfCategory } from '@/lib/apps'

export interface AssistantAnswer {
    paragraphs: Array<string>
    steps?: Array<string>
    cta?: {
        label: string
        message: string
    }
}

// App names that are also ordinary English words only count when the visitor capitalizes them.
const commonWordApps = new Set([
    'close', 'front', 'square', 'wave', 'box', 'x', 'linear', 'tally', 'lever', 'copper', 'particle', 'ramp',
    'sanity', 'notion', 'loom', 'coda', 'guru', 'aspire', 'luma', 'buffer', 'replicate', 'framer', 'cohere',
    'monday', 'bill', 'docs', 'drive', 'sheets', 'teams'
])

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const detectionTerms: Array<{ term: string, app: string }> = [
    ...apps.map(app => ({ term: app.name, app: app.name })),
    ...Object.entries(appAliases).map(([term, app]) => ({ term, app }))
].sort((first, second) => second.term.length - first.term.length)

interface DetectedApp {
    app: AppEntry
    index: number
}

// Finds the apps a visitor mentions, longest names first so "Google Sheets" wins over "Sheets".
export const detectApps = (question: string): Array<AppEntry> => {
    const lower = question.toLowerCase()
    const found: Array<DetectedApp> = []
    const taken: Array<[number, number]> = []

    detectionTerms.forEach(({ term, app }) => {
        const pattern = new RegExp(`(^|[^a-z0-9])(${escapeRegExp(term.toLowerCase())})(?=[^a-z0-9]|$)`, 'g')
        let match = pattern.exec(lower)

        while (match) {
            const start = match.index + match[1].length
            const end = start + match[2].length
            const overlaps = taken.some(([from, to]) => start < to && end > from)
            const original = question.slice(start, end)
            const needsCapital = commonWordApps.has(term.toLowerCase())
            const entry = findApp(app)

            if (!overlaps && entry && (!needsCapital || /^[A-Z]/.test(original))) {
                taken.push([start, end])

                if (!found.some(item => item.app.name === entry.name)) found.push({ app: entry, index: start })
            }

            match = pattern.exec(lower)
        }
    })

    return found.sort((first, second) => first.index - second.index).map(item => item.app)
}

const objectWords: Array<[RegExp, string, string]> = [
    [/sales orders?|orders?/, "orders", "order"],
    [/invoices?/, "invoices", "invoice"],
    [/payouts?/, "payouts", "payout"],
    [/payments?|charges?|transactions?/, "payments", "payment"],
    [/refunds?|returns?/, "refunds", "refund"],
    [/deals?|opportunit(y|ies)/, "deals", "deal"],
    [/leads?/, "leads", "lead"],
    [/contacts?|customers?|clients?/, "customer records", "customer record"],
    [/tickets?|cases?/, "tickets", "ticket"],
    [/products?|items?|catalog/, "products", "product"],
    [/inventory|stock/, "inventory levels", "inventory level"],
    [/shipments?|fulfillments?|tracking/, "shipments", "shipment"],
    [/bookings?|meetings?|appointments?|events?/, "bookings", "booking"],
    [/responses?|submissions?|forms?|surveys?/, "form responses", "form response"],
    [/tasks?|issues?|cards?/, "tasks", "task"],
    [/bills?|expenses?|receipts?/, "bills", "bill"],
    [/employees?|hires?|candidates?/, "people records", "people record"],
    [/messages?|emails?|mentions?/, "messages", "message"],
    [/rows?|records?|data/, "records", "record"]
]

const detectObject = (question: string): [string, string] => {
    const lower = question.toLowerCase()

    for (const [pattern, plural, singular] of objectWords) {
        if (pattern.test(lower)) return [plural, singular]
    }

    return ["records", "record"]
}

const messagingCategories = new Set(['team-messaging', 'email-communications'])

// Works out which app the data comes from and which apps receive it, using "from", "to", "into" and "in" when present.
const pickDirection = (question: string, found: Array<AppEntry>): [AppEntry, Array<AppEntry>] => {
    const lower = question.toLowerCase()
    const named = (app: AppEntry, words: Array<string>) => words.some(word => lower.includes(`${word} ${app.name.toLowerCase()}`))
    const explicitSource = found.find(app => named(app, ['from']))
    const target = found.find(app => app !== explicitSource && named(app, ['to', 'into', 'in', 'inside', 'with']))
    const source = explicitSource ?? found.find(app => app !== target) ?? found[0]
    const targets = found.filter(app => app !== source)

    if (target) targets.sort((first, second) => (first === target ? -1 : second === target ? 1 : 0))

    return [source, targets]
}

const listNames = (items: Array<string>): string => {
    if (items.length <= 1) return items.join('')

    return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
}

const workflowAnswer = (question: string, found: Array<AppEntry>): AssistantAnswer => {
    const [source, targets] = pickDirection(question, found)
    const [objects, object] = detectObject(question)
    const targetNames = listNames(targets.map(app => app.name))
    const lower = question.toLowerCase()
    const posting = targets.every(app => messagingCategories.has(app.category)) || /\b(post|notify|alert|announce|ping)\b/.test(lower)
    const reconciling = /\b(reconcile|reconciliation|match|matching)\b/.test(lower)

    let action = `Create or update: ${targetNames} gets each ${object} as it happens, with duplicates prevented and history kept.`

    if (posting) action = `Notify: a clear message lands in ${targetNames} with the details your team needs to act.`
    if (reconciling) action = `Reconcile: amounts are matched against ${targetNames} and anything that does not line up is flagged for review.`

    return {
        paragraphs: [`Here is how FlowCraft would run "${question.trim()}":`],
        steps: [
            `Trigger: FlowCraft watches ${source.name} for new and updated ${objects}.`,
            `Map: every ${object} is matched to the right fields in ${targetNames}, including your custom fields and business rules.`,
            action,
            "Watch: each run is logged and retried on errors, and our team monitors the workflow for you."
        ],
        cta: {
            label: "Have our team scope this",
            message: question.trim()
        }
    }
}

const categoryExamples: Record<string, Array<string>> = {
    'commerce': ["Sync orders into your ERP or accounting system", "Keep inventory levels in step across every channel", "Send fulfillment and tracking updates back to customers"],
    'sales-crm': ["Create deals from form submissions and booked meetings", "Post won deals to your team chat", "Sync customers with your accounting system"],
    'productivity': ["Turn form responses into tasks and records", "Keep spreadsheets in sync with your business systems", "Create follow-ups when meetings are booked"],
    'communication': ["Alert the right channel when something needs attention", "Share daily summaries from your business systems", "Turn requests into tickets or tasks"],
    'support': ["Open tickets from orders, forms and emails", "Sync customer context from your CRM", "Escalate urgent issues to the right team"],
    'marketing': ["Sync new customers into audiences and segments", "Send conversions back to ad platforms", "Trigger campaigns from purchases and signups"],
    'human-resources': ["Create accounts when new hires start", "Sync people data with payroll and finance", "Route approvals to managers automatically"],
    'it-operations': ["Move records between databases and business apps", "Trigger workflows from events and webhooks", "Alert the team when something fails"],
    'business-intelligence': ["Load business data into your warehouse on a schedule", "Refresh dashboards from live systems", "Share key metrics with your team"],
    'artificial-intelligence': ["Summarize documents and tickets automatically", "Classify and route incoming requests", "Draft replies your team approves"],
    'content-files': ["File documents into the right folders automatically", "Turn uploaded files into structured records", "Share files with customers as work completes"],
    'website-app-building': ["Publish content from your business systems", "Send form leads into your CRM", "Keep product and pricing pages up to date"]
}

const singleAppAnswer = (app: AppEntry): AssistantAnswer => {
    const group = groupOfCategory(app.category) ?? 'productivity'
    const examples = categoryExamples[group] ?? categoryExamples.productivity
    const capability = (app.description.charAt(0).toLowerCase() + app.description.slice(1)).replace(/\.$/, '')

    return {
        paragraphs: [
            `FlowCraft connects to ${app.name} out of the box, so your workflows can ${capability}.`,
            `Popular ${app.name} workflows our team builds:`
        ],
        steps: examples,
        cta: {
            label: `Plan a ${app.name} workflow`,
            message: `I would like to automate work with ${app.name}.`
        }
    }
}

interface Topic {
    pattern: RegExp
    withApp?: boolean
    answer: AssistantAnswer
}

const talk = (message: string, label = "Talk to our team") => ({ label, message })

const topics: Array<Topic> = [
    {
        pattern: /(price|pricing|cost|how much|budget|expensive|quote)/,
        withApp: true,
        answer: {
            paragraphs: [
                "Pricing depends on the systems, the workflows, your data volume and how complex the logic is.",
                "We scope your requirements first and price the work upfront, so you know exactly what to expect before anything moves forward."
            ],
            cta: talk("I would like a quote for an integration project.", "Get a scoped quote")
        }
    },
    {
        pattern: /(how long|timeline|how fast|how quickly|weeks|time to launch|go live)/,
        withApp: true,
        answer: {
            paragraphs: [
                "Many integrations go live in a matter of weeks. The exact timeline depends on the systems and workflows involved.",
                "During discovery we define the scope and give you a clear plan and timeline before any development begins."
            ],
            cta: talk("I would like to understand the timeline for my integration.")
        }
    },
    {
        pattern: /(after go-live|after go live|after launch|after it launches|maintain|maintenance|monitor)/,
        withApp: true,
        answer: {
            paragraphs: [
                "We stay involved after launch. FlowCraft monitors every run, keeps connections fresh when credentials or APIs change, meets your team for regular check-ins and tunes workflows as volumes grow."
            ]
        }
    },
    {
        pattern: /(api change|changes its api|change their api|breaking change|deprecat)/,
        withApp: true,
        answer: {
            paragraphs: [
                "API changes are a normal part of running integrations. Because support is ongoing, our team updates the workflows as your systems evolve, so nobody on your side has to troubleshoot or rebuild anything."
            ]
        }
    },
    {
        pattern: /(no connector|without a connector|custom connector|prebuilt|pre-built|legacy|homegrown|in-house system|\bsftp\b|\bedi\b|database|\bcsv\b)/,
        withApp: true,
        answer: {
            paragraphs: [
                "Yes. FlowCraft is not limited to a fixed library of connectors. If a system offers a secure way to exchange data, such as an API, a database, a webhook or a file exchange, our engineers can usually build a custom connection for it."
            ],
            cta: talk("We need to connect a system that has no prebuilt connector.")
        }
    },
    {
        pattern: /(custom field|business logic|transform|conditional|routing rule|multi-step|multi step|edge case)/,
        withApp: true,
        answer: {
            paragraphs: [
                "Yes. Every workflow is shaped around your process, including custom fields, data transformations, routing rules, conditional logic and multi-step flows."
            ]
        }
    },
    {
        pattern: /(security|secure|soc ?2|hipaa|gdpr|encrypt|compliance|privacy)/,
        withApp: true,
        answer: {
            paragraphs: [
                "Security is part of how every workflow is designed: access is scoped to what each integration needs, credentials are stored encrypted and every run is logged.",
                "If your security team needs details, we are happy to walk them through it."
            ],
            cta: talk("Our security team has questions about FlowCraft.", "Book a security review")
        }
    },
    {
        pattern: /(full service|full-service|done for you|managed|include|\bsupport\b)/,
        answer: {
            paragraphs: [
                "Full service pairs our integration platform with a dedicated crew: Solutions Engineers scope the work, an Integration Engineer builds it on Studio, and Support monitors and maintains it after go-live.",
                "It is an all-inclusive annual subscription with dedicated support, a technical SLA and three hours of professional services every month."
            ],
            cta: talk("I would like to learn more about full-service integrations.")
        }
    },
    {
        pattern: /(pilot|personal agent|productivity agent|knowledge worker)/,
        answer: {
            paragraphs: [
                "Pilot is the FlowCraft assistant for knowledge workers. Connect your apps, show Pilot the steps and choices that make up a task, and Pilot turns your process into a workflow that runs on its own, with no code and no technical handoff."
            ],
            cta: talk("I would like to try Pilot with my team.", "Try Pilot with your team")
        }
    },
    {
        pattern: /(studio|builder|low-code|low code|visual)/,
        answer: {
            paragraphs: [
                "Studio is our visual integration builder. It is where our engineers build your workflows, and where your team can build and adjust integrations visually too."
            ]
        }
    },
    {
        pattern: /(one api|unified api|single api|rest api|developer|sdk|endpoint)/,
        answer: {
            paragraphs: [
                "One API gives your developers a single, consistent API for 4,100+ apps and 81,000 actions. Every response comes back in the same clean format, whatever the app underneath returns."
            ],
            cta: talk("I am interested in One API for our product.", "Talk to an engineer")
        }
    },
    {
        pattern: /(technology|ai-first|ai first|architecture|under the hood|built on)/,
        answer: {
            paragraphs: [
                "FlowCraft was built AI-first as one unified system. Instead of adding AI on top of an older platform, every part was designed to work together from the start, which gives our team a solid foundation for integrations that stay reliable."
            ]
        }
    },
    {
        pattern: /(different|compare|competitor|versus|\bvs\b|why flowcraft|ipaas|alternative)/,
        answer: {
            paragraphs: [
                "You get the people and the platform together. Our in-house team scopes, builds, launches, monitors and supports your integrations on our own platform, so you have one accountable partner instead of software you manage alone."
            ]
        }
    },
    {
        pattern: /(get started|getting started|start|begin|onboard|discovery|sign up|signup)/,
        answer: {
            paragraphs: [
                "Start with a discovery call. We review the systems you want to connect, the data that needs to move between them and your business rules, then recommend an approach and define the scope."
            ],
            cta: talk("I would like to book a discovery call.", "Book a discovery call")
        }
    },
    {
        pattern: /(what systems|which systems|what apps|which apps|what can you connect|what do you connect|supported|integrations do you)/,
        answer: {
            paragraphs: [
                "FlowCraft connects the systems that run your operations: ERP, ecommerce, CRM, accounting, warehouse and shipping, field service, payroll, EDI and marketplaces, 4,100+ apps in total.",
                "Name two of your tools and I will sketch a workflow between them."
            ]
        }
    }
]

const fallback: AssistantAnswer = {
    paragraphs: [
        "FlowCraft connects the tools your business runs on and builds the workflows between them, with a team that scopes, builds and supports every one.",
        "Tell me which apps you use, for example \"Sync Shopify orders into NetSuite\", and I will sketch how the workflow would run."
    ],
    cta: talk("I have a question about FlowCraft.")
}

// Scripted answers: two or more apps get a workflow sketch, one app gets its common workflows, everything else is matched to a topic.
export const answerQuestion = (question: string): AssistantAnswer => {
    const lower = question.toLowerCase()
    const found = detectApps(question)

    if (found.length >= 2) return workflowAnswer(question, found)

    const topic = topics.find(item => item.pattern.test(lower) && (found.length === 0 || item.withApp))

    if (topic) return topic.answer
    if (found.length === 1) return singleAppAnswer(found[0])

    return fallback
}

export const assistantSuggestions = [
    "How does FlowCraft connect to NetSuite?",
    "What does full service include?",
    "How long does a typical project take?"
]
